import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { TABLE_ROWS_PER_PAGE, TABLE_DATE_FORMAT, TABLE_TIME_FORMAT } from "../../../../utils/Constants";
import { RenderLearningMethod } from "../../../../utils/CommonGroupingItem";
import { RenderMatchStatus } from "../../../../utils/CommonStatusItems";
import DataTable from "react-data-table-component";
import SkeletonTicketList from "../../../../loaders/SkeletonTicketList";
import PermissionsGate from "../../../../utils/permissionGate";
import moment from "moment";
import Swal from "sweetalert2";
import { SwitchRegistrationModuleTerm } from "../../../../services/RegistrationService";

const safeToString = (v) => (v === null || v === undefined ? "" : v.toString());


const getMismatchTooltip = (row) => {
  return `Learning method mismatch.`;
};

const MatchedCoursesTable = ({ courses, qualOption, currentTerm, registrationId, rawStatus, onSwitched }) => {
  // Helper to get label from the qualOption array (passed as prop)
  const getQualLabel = (netsuiteId) => {
    const option = qualOption.find(
      (o) => safeToString(o.value) === safeToString(netsuiteId)
    );
    return option ? option.label : "Netsuite Product not Found";
  };

  const canSwitchTerm = ["Re-Registration-Sales Admin", "Re-Reg Re-Posted"].includes(rawStatus);
  const targetTerm = currentTerm === 1 ? 2 : 1;

  /**
   * Moves a matched module to the other term, after user confirmation, then triggers a refetch
   * of the registration details so the module reappears in the correct term's table.
   * @param {Object} row A matched-courses row (see columns below for shape)
   */
  const handleSwitchTerm = (row) => {
    Swal.fire({
      icon: "warning",
      title: "Move module?",
      text: `Move "${row.Name !== "-" ? row.Name : row.NetsuiteId}" to Term ${targetTerm}? This changes which course will be enrolled for the student.`,
      showCancelButton: true,
      confirmButtonText: "Yes, move it",
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }
      SwitchRegistrationModuleTerm({
        registration_id: registrationId,
        netsuite_product_id: row.NetsuiteId,
        term: targetTerm,
      })
        .then((response) => {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: response.data.message,
          });
          if (onSwitched) {
            onSwitched();
          }
        })
        .catch((error) => {
          console.error("[MatchedCoursesTable.js/handleSwitchTerm]", error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: error?.response?.data?.message ?? "Unable to switch this module's term.",
          });
        });
    });
  };

  // Column definitions (using useMemo for optimization)
  const columns = useMemo(() => [
    {
      name: "Course",
      selector: "Name",
      sortable: true,
      cell: row => (
        <div>
          {!row.intakeId ? (
            <strong>{getQualLabel(row.NetsuiteId)}</strong>
          ) : (
            <Link
              to={`/courseAdministration/coursesdetails/${row.intakeId}/detailCourses/show`}
              className="as-text-blue textLimit50"
              title={row.Name}
            >
              {row.Name}
            </Link>
          )}
          {row.isTermSwitched && (
            <p className="mb-0 text-muted" style={{ fontSize: "12px" }}>
              Matched term modified by <b>{row.switchedByName ?? "Unknown"}</b> on{" "}
              <b>
                {moment(row.switchedAt).format(TABLE_DATE_FORMAT)} {moment(row.switchedAt).format(TABLE_TIME_FORMAT)}
              </b>
            </p>
          )}
        </div>
      )
    },
    {
      name: "Status",
      selector: "Status",
      cell: row => RenderMatchStatus(row.Status).html
    },
    {
      name: "Netsuite ID",
      selector: "NetsuiteId",
      cell: row => <span className="titleTextLimit30">{row.NetsuiteId}</span>
    },
    {
      name: "Intake",
      selector: "Intake",
      cell: row => <span className="titleTextLimit50">{row.Intake}</span>
    },
    {
      name: "Start",
      selector: "StartDate",
      cell: row => <div className="">
      {row.Start != "-" ? <>
      <p className="right-space">
        {moment.unix(row.Start).format(TABLE_DATE_FORMAT)}
        </p>
      </> : <span>"-"</span>
      }
      </div>
    },
    {
      name: "End",
      selector: "EndDate",
      cell: row => <div className="">
      {row.End != "-" ? <>
      <p className="right-space">
        {moment.unix(row.End).format(TABLE_DATE_FORMAT)}
        </p>
      </> : <span>"-"</span>
      }
      </div>
    },
    {
      name: "Learning Method",
      selector: "Type",
      cell: row => {
        const showWarning = row.learningMethodMismatch === true;
        
        return (
          <div className="d-flex align-items-center">
            {RenderLearningMethod(row.Type).html}
            
            {showWarning && (
              <i
                className="fal fa-exclamation-triangle ml-2 text-danger"
                title={getMismatchTooltip(row)}
                style={{ cursor: "help" }}
              />
            )}
          </div>
        );
      }
    },
    {
      name: "Actions",
      cell: row => (
        <div className="assessment-08 btn-dropdown-grp">
        <div className="as-buttons">
        {row.intakeId ? (
          <PermissionsGate
          scopes={["cview"]}
          RenderError={() => (
            <button className="btn btn-primary rounded-circle" title="Open" disabled>
            <i className="fal fa-folder-open"></i>
            </button>
          )}
          >
          <Link
          to={`/courseAdministration/coursesdetails/${row.intakeId}/detailCourses/show`}
          className="btn btn-primary rounded-circle"
          title="Open"
          >
          <i className="fal fa-folder-open"></i>
          </Link>
          </PermissionsGate>
        ) : (
          <button className="btn btn-primary rounded-circle" title="Open" disabled>
          <i className="fal fa-folder-open"></i>
          </button>
        )}
        {canSwitchTerm && (
          <PermissionsGate
          scopes={["regtermedit"]}
          errorProps={{ disabled: true }}
          >
          <button
          className="btn btn-primary rounded-circle"
          type="button"
          title={`Move to Term ${targetTerm}`}
          onClick={() => handleSwitchTerm(row)}
          >
          <i className="fal fa-exchange"></i>
          </button>
          </PermissionsGate>
        )}
        </div>
        </div>
      )
    }
  ], [courses, qualOption, canSwitchTerm, targetTerm, registrationId]);
  
  return (
    <div className="l-o-c-t custom-table-div filter-search-icon card card-table-custom days-overdue-cell-width">
    <div className="card-header">Matched Courses</div>
    <DataTable
    data={courses}
    defaultSortField="Name"
    defaultSortAsc
    columns={columns}
    pagination
    paginationRowsPerPageOptions={TABLE_ROWS_PER_PAGE}
    progressComponent={<SkeletonTicketList />}
    progressPending={false}
    />
    </div>
  );
};

export default MatchedCoursesTable;
